const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
// Kebab-case editorial slugs can be long and are useful in analytics. Opaque
// identifiers are compact unbroken strings; capability families are redacted
// separately below even when their token happens to contain a dash.
const OPAQUE_SEGMENT = /^[A-Za-z0-9_.~]{24,}$/;

/**
 * Capability URLs are credentials. Treat their path segments with the same
 * care as query-string access tokens, even when a telemetry SDK records a
 * pathname automatically.
 */
export function isSensitiveCapabilityPath(pathname: string): boolean {
  return (
    /^\/share\/[^/]+/i.test(pathname) ||
    /^\/intake\/upload(?:\/|$)/i.test(pathname) ||
    /^\/intake\/(?!success(?:\/|$)|upload(?:\/|$))[^/]+/i.test(pathname)
  );
}

/** Keep route shape for analysis while removing bearer tokens and IDs. */
export function sanitizeTelemetryPath(pathname: string): string {
  const withoutQuery = pathname.split("?")[0]?.split("#")[0] ?? pathname;
  let safePath = withoutQuery
    .replace(/^\/share\/[^/]+/i, "/share/:token")
    .replace(/^\/intake\/upload\/[^/]+/i, "/intake/upload/:token")
    .replace(
      /^\/intake\/(?!success(?:\/|$)|upload(?:\/|$))[^/]+/i,
      "/intake/:token",
    );

  safePath = safePath
    .split("/")
    .map((segment) =>
      UUID_SEGMENT.test(segment) || OPAQUE_SEGMENT.test(segment) ? ":id" : segment,
    )
    .join("/");

  return safePath.slice(0, 500);
}

/** Strip query/hash and redact sensitive path segments in absolute or local URLs. */
export function sanitizeTelemetryUrl(input: string): string {
  const isAbsolute = /^[a-z][a-z\d+.-]*:\/\//i.test(input);
  try {
    const url = new URL(input, "https://telemetry.invalid");
    const pathname = sanitizeTelemetryPath(url.pathname);
    return isAbsolute ? `${url.origin}${pathname}` : pathname;
  } catch {
    return sanitizeTelemetryPath(input);
  }
}

/**
 * Vercel Analytics and Speed Insights both expose a before-send hook. Keep
 * their native event shape while removing bearer capabilities, query strings,
 * and opaque record identifiers before an event leaves the browser.
 */
export function sanitizeVercelTelemetryEvent<T extends { url: string; route?: string }>(
  event: T,
): T {
  return {
    ...event,
    url: sanitizeTelemetryUrl(event.url),
    ...(typeof event.route === "string"
      ? { route: sanitizeTelemetryPath(event.route) }
      : {}),
  };
}

const NORMALIZED_PATH_PROPERTY_KEYS = new Set([
  "pathname",
  "path",
  "targetpath",
  "landingpath",
  "firsttouchlandingpath",
]);

const NORMALIZED_URL_PROPERTY_KEYS = new Set([
  "currenturl",
  "referrer",
  "blockeduri",
  "documenturi",
  "href",
  "sourcefile",
  "url",
]);

// PostHog's own anonymous linkage keys are operational rather than product
// identifiers. Explicit product/entity identifiers must never leave the app.
//
// `anondistinctid` is `$anon_distinct_id`, the pre-signup anonymous id that
// posthog-js sends on `$create_alias`. Without it the generic `endsWith("id")`
// rule below deletes it, `alias()` silently no-ops, and the anonymous→identified
// stitching that posthog-provider.tsx performs on sign-in cannot happen — every
// funnel spanning signup shows a discontinuity. It is PostHog's own linkage key,
// not a product identifier, so allowing it does not weaken the leak guard.
//
// `aitraceid` is `$ai_trace_id`, which PostHog LLM Analytics REQUIRES on every
// `$ai_generation` event — without it the event is not a valid generation and
// never reaches the model/cost views. It is our own request id, not a product
// identifier.
//
// `runid` is `ai_runs.id`, an opaque UUID for one processing job. It is the
// ONLY join key between the `*_started_server` and `*_completed_server` legs of
// the generation and gap-analysis funnels; without it "how many founders
// started an analysis and how many got one", and every duration query built on
// that pair, are unanswerable. Same class as `requestid` above — a handle on
// our own work, with no link to a person.
//
// `jobid` is `jobs.id`, the opaque UUID of one background job. Same class as
// `runid`: a handle on our own work, and the only way to go from a
// dead-lettered job's `$exception` to its row and logs.
//
// `orgid` is `organizations.id`. It discloses nothing new: the same value is
// already the `distinct_id` (`org:<uuid>`) and the PostHog GROUP key on these
// events. It is allowed here for the ONE path that cannot carry a group —
// posthog-node 5.x's `captureException()` takes no `groups` argument, and
// `capture()` overwrites `$groups` from the event message, so a server
// `$exception` had no org attribution at all in Error Tracking.
//
// Deliberately NOT here: `leadid`, `projectid`, `deckid` and friends. Those
// identify customer records, and the pins in `__tests__/privacy.test.ts` keep
// them dropped.
const SAFE_OPERATIONAL_ID_KEYS = new Set([
  "aitraceid",
  "anondistinctid",
  "deviceid",
  "distinctid",
  "experimentid",
  "jobid",
  "orgid",
  "requestid",
  "runid",
  "sessionid",
  // Svix's webhook DELIVERY id, carried on the Clerk-webhook events. Provider
  // infrastructure metadata with no customer content — it identifies a message
  // on the wire, not a person — and the only handle for "did this webhook
  // double-fire?" once the event is in PostHog.
  "svixeventid",
]);

const SENSITIVE_PROPERTY_KEYS = new Set([
  "authorization",
  "body",
  "company",
  "companyname",
  "content",
  "cookie",
  "deckcontent",
  "deckmarkdown",
  "email",
  "emailaddress",
  "exceptionlist",
  "exceptionmessage",
  "exceptionstacktraceraw",
  "filename",
  "fullname",
  "html",
  "markdown",
  "message",
  "name",
  "password",
  "prompt",
  "query",
  "rawresponse",
  "reportcontent",
  "reportmarkdown",
  "secret",
  "storagepath",
  "subject",
  "text",
  "token",
  "transcript",
]);

const MAX_TELEMETRY_DEPTH = 5;

/**
 * PostHog's reserved (`$`-prefixed) properties share a namespace with app
 * properties once normalised — `$exception_message` collapses to
 * "exceptionmessage", which the app-property rules below deny. That collision
 * silently reverted the 2026-05-28 fix that promotes the message to a
 * top-level property so PostHog's property-filter alerting can match on it.
 * (`$exception_type` survives only because "exceptiontype" happens not to be
 * a member — the same hazard applies to any reserved key added later.)
 *
 * `$exception_message` is re-permitted deliberately, and it discloses nothing
 * new: posthog-node builds the identical string into `$exception_list[].value`
 * internally, which never passes through this sanitizer. Confirmed against a
 * real project export — ingested events carry `$exception_type` and
 * `$exception_values.0` while `$exception_message` is absent. Denying the
 * top-level copy hid the value from alerting without hiding it from PostHog.
 *
 * Matched on the RAW key so this stays a narrow, explicit carve-out rather
 * than a blanket "allow anything starting with $" (`$exception_list` must
 * keep being dropped — it can carry customer input).
 */
const ALLOWED_RESERVED_PROPERTY_KEYS = new Set(["$exception_message"]);

function normalizePropertyKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function shouldDropTelemetryProperty(key: string): boolean {
  if (ALLOWED_RESERVED_PROPERTY_KEYS.has(key)) return false;
  const normalized = normalizePropertyKey(key);
  return (
    SENSITIVE_PROPERTY_KEYS.has(normalized)
    || (normalized.endsWith("id") && !SAFE_OPERATIONAL_ID_KEYS.has(normalized))
    || normalized.endsWith("promocode")
    || normalized.endsWith("promotioncode")
    || normalized.endsWith("referralcode")
    || normalized.endsWith("couponcode")
    || normalized.endsWith("invitecode")
    || normalized.endsWith("email")
    || normalized.endsWith("token")
    || normalized.endsWith("secret")
    || normalized.endsWith("storagepath")
  );
}

function sanitizeTelemetryValue(
  value: unknown,
  depth: number,
): unknown {
  if (depth > MAX_TELEMETRY_DEPTH) return "[REDACTED_DEPTH]";

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeTelemetryValue(item, depth + 1));
  }

  if (value && typeof value === "object") {
    return sanitizeTelemetryProperties(
      value as Record<string, unknown>,
      depth + 1,
    );
  }

  return value;
}

/** Defence-in-depth for explicit events and SDK-generated page/error events. */
export function sanitizeTelemetryProperties(
  properties: Record<string, unknown>,
  depth = 0,
): Record<string, unknown> {
  for (const [key, value] of Object.entries(properties)) {
    const normalizedKey = normalizePropertyKey(key);
    if (shouldDropTelemetryProperty(key)) {
      delete properties[key];
      continue;
    }

    if (typeof value === "string") {
      if (NORMALIZED_PATH_PROPERTY_KEYS.has(normalizedKey)) {
        properties[key] = sanitizeTelemetryPath(value);
      } else if (NORMALIZED_URL_PROPERTY_KEYS.has(normalizedKey)) {
        properties[key] = sanitizeTelemetryUrl(value);
      }
      continue;
    }

    properties[key] = sanitizeTelemetryValue(value, depth);
  }
  return properties;
}

/**
 * rrweb `EventType.Meta` — the event that carries the page URL into a
 * recording. Numeric because rrweb ships the enum as a value we would
 * otherwise have to import from the recorder bundle.
 */
const RRWEB_META_EVENT_TYPE = 4;

/**
 * Scrub a `$snapshot` (session replay) event.
 *
 * MUST NOT go through `sanitizeTelemetryProperties`. That function is built
 * for flat funnel-event properties and every one of its rules is destructive
 * when applied to an rrweb payload:
 *
 *   - `shouldDropTelemetryProperty` deletes any key ending in "id", which is
 *     the `id` on EVERY serialised DOM node, plus `$window_id` (replay
 *     ingestion requires it).
 *   - `MAX_TELEMETRY_DEPTH` replaces everything below depth 5 with
 *     "[REDACTED_DEPTH]" — a serialised DOM tree is far deeper than that, so
 *     most of the page would become a string.
 *   - `SENSITIVE_PROPERTY_KEYS` contains `name`, `content`, `text`, `html`,
 *     `body` and `title`, which are ordinary keys inside an `attributes` map.
 *
 * The result would ingest and then fail to play, and the recursive walk would
 * run over a multi-thousand-node array on the main thread for every batch.
 * So the payload is left structurally intact and we scrub only the place a
 * capability token can actually appear.
 *
 * What this DOES redact: the page URL. `/share/<token>`, `/intake/<token>`
 * and `/intake/upload/<token>` are bearer capabilities — a recording that
 * stores one verbatim stores a credential. `sanitizeTelemetryUrl` already
 * collapses all three shapes (plus UUID and opaque segments) and is the same
 * helper the funnel-event path uses.
 *
 * What this deliberately does NOT redact: on-screen text. Masking for replay
 * is configured on the recorder itself (`session_recording` in
 * posthog-provider.tsx), not here — see ADR-0023.
 */
export function sanitizeSnapshotProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  const currentUrl = properties.$current_url;
  if (typeof currentUrl === "string") {
    properties.$current_url = sanitizeTelemetryUrl(currentUrl);
  }

  const snapshotData = properties.$snapshot_data;
  if (Array.isArray(snapshotData)) {
    for (const entry of snapshotData) {
      if (!entry || typeof entry !== "object") continue;
      const rrwebEvent = entry as { type?: unknown; data?: unknown };
      if (rrwebEvent.type !== RRWEB_META_EVENT_TYPE) continue;

      const data = rrwebEvent.data as Record<string, unknown> | undefined;
      if (!data || typeof data !== "object") continue;
      if (typeof data.href === "string") {
        data.href = sanitizeTelemetryUrl(data.href);
      }
    }
  }

  return properties;
}

/**
 * SDK click-level events enabled for full-consent visitors by ADR-0024.
 * `$rageclick` comes from autocapture; `$dead_swipe` shares the dead-click
 * extension and its property shape.
 */
const CLICK_TELEMETRY_EVENTS = new Set([
  "$autocapture",
  "$rageclick",
  "$dead_click",
  "$dead_swipe",
  "$$heatmap",
]);

export function isClickTelemetryEvent(event: string): boolean {
  return CLICK_TELEMETRY_EVENTS.has(event);
}

/** `attr__href="…"` / `href="…"` inside a serialised `$elements_chain`. */
const HREF_IN_ELEMENTS_CHAIN = /((?:attr__)?href=")((?:[^"\\]|\\.)*)(")/g;

/**
 * Scrub an autocapture / rage-click / dead-click / heatmap event.
 *
 * The generic sanitiser alone is WRONG for these, in two opposite ways:
 *
 *   - It under-scrubs. posthog-js copies an anchor's `href` into the element
 *     (`attr__href`) even with `mask_all_element_attributes` on, and repeats it
 *     in `$elements_chain` (a serialised string) and `$external_click_url`.
 *     None of those keys is URL-shaped to the generic rules, so a
 *     `/share/<token>` capability would ship verbatim.
 *   - It over-scrubs. `$$heatmap` stores its points in `$heatmap_data`, keyed by
 *     the raw page URL. Cleaned, a key like `/portal/projects/:id` normalises
 *     to one ending in "id" and the generic key rule would delete the heatmap.
 *
 * So: scrub hrefs first, take the heatmap map out of the generic walk and clean
 * its URL KEYS instead, and let the generic rules handle everything else.
 */
export function sanitizeClickTelemetryProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  const chain = properties.$elements_chain;
  if (typeof chain === "string") {
    properties.$elements_chain = chain.replace(
      HREF_IN_ELEMENTS_CHAIN,
      (_match, open: string, value: string, close: string) =>
        `${open}${sanitizeTelemetryUrl(value)}${close}`,
    );
  }

  const elements = properties.$elements;
  if (Array.isArray(elements)) {
    for (const element of elements) {
      if (!element || typeof element !== "object") continue;
      const record = element as Record<string, unknown>;
      for (const key of ["attr__href", "href"]) {
        const value = record[key];
        if (typeof value === "string") record[key] = sanitizeTelemetryUrl(value);
      }
    }
  }

  if (typeof properties.$external_click_url === "string") {
    properties.$external_click_url = sanitizeTelemetryUrl(properties.$external_click_url);
  }

  const heatmap = properties.$heatmap_data;
  delete properties.$heatmap_data;
  sanitizeTelemetryProperties(properties);

  if (heatmap && typeof heatmap === "object" && !Array.isArray(heatmap)) {
    const cleaned: Record<string, unknown> = {};
    for (const [url, points] of Object.entries(heatmap as Record<string, unknown>)) {
      const safeUrl = sanitizeTelemetryUrl(url);
      const existing = cleaned[safeUrl];
      // Two raw URLs can collapse to one cleaned URL — merge, never overwrite.
      cleaned[safeUrl] =
        Array.isArray(existing) && Array.isArray(points) ? [...existing, ...points] : points;
    }
    properties.$heatmap_data = cleaned;
  }

  return properties;
}
